import { useCallback, useMemo, useState } from 'react';
import { createContext, useContext } from 'react';

import {
  attachSearchPresetType,
  matchesSearchPresetType,
  stripSearchPresetType,
} from '@modules/search-presets/presetTypes';
import { useSearchPresets } from './hooks/useSearchPresets';
import { ITalentSearchFilter, SearchPreset } from './types';

interface SearchPresetContextValue {
  presets: SearchPreset[];
  personalPresets: SearchPreset[];
  sharedPresets: SearchPreset[];
  loading: boolean;
  creating: boolean;
  deleting: boolean;
  anchorEl: HTMLElement | null;
  saveDialogOpen: boolean;
  presetName: string;
  presetDescription: string;
  isShared: boolean;
  openChooseMenu: (anchor: HTMLElement) => void;
  closeChooseMenu: () => void;
  openSaveDialog: () => void;
  closeSaveDialog: () => void;
  setPresetName: (name: string) => void;
  setPresetDescription: (description: string) => void;
  setIsShared: (shared: boolean) => void;
  saveCurrentPreset: () => Promise<void>;
  applyPreset: (preset: SearchPreset) => void;
  deletePresetById: (id: string) => Promise<void>;
}

const SearchPresetContext = createContext<SearchPresetContextValue | null>(null);

export const useSearchPresetContext = (): SearchPresetContextValue => {
  const ctx = useContext(SearchPresetContext);

  if (!ctx) {
    throw new Error('useSearchPresetContext must be used within a <SearchPresetProvider>');
  }

  return ctx;
};

interface SearchPresetProviderProps {
  currentFilters: ITalentSearchFilter;
  onApplyPreset: (filters: ITalentSearchFilter) => void;
  children: React.ReactNode;
}

export const SearchPresetProvider = ({
  currentFilters,
  onApplyPreset,
  children,
}: SearchPresetProviderProps) => {
  const { presets, loading, creating, deleting, createPreset, deletePreset } = useSearchPresets<ITalentSearchFilter>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [isShared, setIsShared] = useState(false);

  const resetSaveForm = useCallback(() => {
    setPresetName('');
    setPresetDescription('');
    setIsShared(false);
  }, []);

  const openChooseMenu = useCallback((anchor: HTMLElement) => {
    setAnchorEl(anchor);
  }, []);

  const closeChooseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const openSaveDialog = useCallback(() => {
    setSaveDialogOpen(true);
  }, []);

  const closeSaveDialog = useCallback(() => {
    setSaveDialogOpen(false);
    resetSaveForm();
  }, [resetSaveForm]);

  const saveCurrentPreset = useCallback(async () => {
    const trimmedName = presetName.trim();

    if (!trimmedName) {
      return;
    }

    await createPreset(
      trimmedName,
      attachSearchPresetType(currentFilters, 'talent-search'),
      isShared ? 'SHARED' : 'PRIVATE',
      presetDescription.trim() || undefined,
    );

    setSaveDialogOpen(false);
    resetSaveForm();
  }, [presetName, createPreset, currentFilters, isShared, presetDescription, resetSaveForm]);

  const applyPreset = useCallback(
    (preset: SearchPreset) => {
      onApplyPreset(stripSearchPresetType(preset.filters));
      closeChooseMenu();
    },
    [closeChooseMenu, onApplyPreset],
  );

  const deletePresetById = useCallback(
    async (id: string) => {
      await deletePreset(id);
    },
    [deletePreset],
  );

  const compatiblePresets = useMemo(
    () => presets.filter((preset) => matchesSearchPresetType(preset.filters, 'talent-search', true)),
    [presets],
  );

  const personalPresets = useMemo(
    () => compatiblePresets.filter((preset) => preset.visibility === 'PRIVATE'),
    [compatiblePresets],
  );

  const sharedPresets = useMemo(
    () => compatiblePresets.filter((preset) => preset.visibility === 'SHARED'),
    [compatiblePresets],
  );

  const value = useMemo(
    () => ({
      presets: compatiblePresets,
      personalPresets,
      sharedPresets,
      loading,
      creating,
      deleting,
      anchorEl,
      saveDialogOpen,
      presetName,
      presetDescription,
      isShared,
      openChooseMenu,
      closeChooseMenu,
      openSaveDialog,
      closeSaveDialog,
      setPresetName,
      setPresetDescription,
      setIsShared,
      saveCurrentPreset,
      applyPreset,
      deletePresetById,
    }),
    [
      compatiblePresets,
      personalPresets,
      sharedPresets,
      loading,
      creating,
      deleting,
      anchorEl,
      saveDialogOpen,
      presetName,
      presetDescription,
      isShared,
      openChooseMenu,
      closeChooseMenu,
      openSaveDialog,
      closeSaveDialog,
      saveCurrentPreset,
      applyPreset,
      deletePresetById,
    ],
  );

  return <SearchPresetContext.Provider value={value}>{children}</SearchPresetContext.Provider>;
};
